import { useCallback, useEffect, useMemo, useState } from "react";
import { adminApi } from "../lib/adminApi";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  Users,
  Eye,
  UserCheck,
  Inbox,
  Percent,
  Globe,
  RefreshCw,
} from "lucide-react";

function localDate(d = new Date()) {
  return d.toLocaleDateString("en-CA");
}

const PRESETS = [
  { key: "today", label: "Today" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "month", label: "This month" },
  { key: "custom", label: "Custom" },
];

function presetRange(key) {
  const now = new Date();
  const end = localDate(now);
  if (key === "today") return { start: end, end };
  if (key === "7d") {
    const s = new Date(now);
    s.setDate(s.getDate() - 6);
    return { start: localDate(s), end };
  }
  if (key === "month") {
    return { start: localDate(new Date(now.getFullYear(), now.getMonth(), 1)), end };
  }
  const s = new Date(now);
  s.setDate(s.getDate() - 29);
  return { start: localDate(s), end };
}

const CTA_LABELS = {
  promo_cta_clicked: "Promo bar CTA",
  book_trial_clicked: "Book trial",
  request_info_clicked: "Request info",
  view_program_clicked: "View program",
  view_camp_clicked: "View camp",
};

function SummaryCard({ icon: Icon, label, value, testId }) {
  return (
    <div
      className="rounded-2xl bg-white border border-slate-200 p-5"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 text-slate-500">
        <Icon className="h-4 w-4" />
        <p className="text-[11px] uppercase tracking-widest font-bold">{label}</p>
      </div>
      <p className="mt-2 font-display text-2xl font-extrabold text-slate-900 truncate">
        {value}
      </p>
    </div>
  );
}

function RankTable({ title, rows, labelKey, testId, empty = "No data yet" }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={testId}>
      <h3 className="font-display text-sm font-extrabold text-slate-900 uppercase tracking-wider">
        {title}
      </h3>
      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">{empty}</p>
      ) : (
        <ul className="mt-3 space-y-2.5">
          {rows.map((r, i) => (
            <li key={i} className="text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="truncate text-slate-700 font-medium">
                  {r[labelKey] || "—"}
                </span>
                <span className="font-bold text-slate-900 shrink-0">{r.count}</span>
              </div>
              <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full rounded-full bg-sky-600"
                  style={{ width: `${(r.count / max) * 100}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AdminAnalyticsPage() {
  const [preset, setPreset] = useState("30d");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const range = useMemo(() => {
    if (preset === "custom") {
      if (!custom.start || !custom.end) return null;
      return custom;
    }
    return presetRange(preset);
  }, [preset, custom]);

  const load = useCallback(async () => {
    if (!range) return;
    setLoading(true);
    setError("");
    try {
      const { data: d } = await adminApi.get("/admin/analytics/summary", {
        params: { start: range.start, end: range.end },
      });
      setData(d);
    } catch {
      setError("Couldn't load analytics. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const s = data?.summary;

  return (
    <div data-testid="admin-analytics-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">
            Analytics
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Privacy-friendly website analytics — anonymous visitors only, no personal data.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50"
          data-testid="analytics-refresh"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      {/* Date range filter */}
      <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="analytics-range-filter">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPreset(p.key)}
            data-testid={`analytics-range-${p.key}`}
            className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${
              preset === p.key
                ? "bg-slate-900 text-white"
                : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
            }`}
          >
            {p.label}
          </button>
        ))}
        {preset === "custom" && (
          <div className="flex items-center gap-2 ml-1">
            <input
              type="date"
              value={custom.start}
              max={custom.end || undefined}
              onChange={(e) => setCustom((c) => ({ ...c, start: e.target.value }))}
              className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700"
              data-testid="analytics-custom-start"
            />
            <span className="text-xs text-slate-400">to</span>
            <input
              type="date"
              value={custom.end}
              min={custom.start || undefined}
              onChange={(e) => setCustom((c) => ({ ...c, end: e.target.value }))}
              className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700"
              data-testid="analytics-custom-end"
            />
          </div>
        )}
      </div>

      {error && (
        <p className="mt-6 text-sm font-semibold text-rose-600" data-testid="analytics-error">
          {error}
        </p>
      )}

      {loading && !data ? (
        <p className="mt-8 text-sm text-slate-500" data-testid="analytics-loading">
          Loading analytics…
        </p>
      ) : data ? (
        <>
          {/* Summary cards */}
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="analytics-summary-cards">
            <SummaryCard icon={Users} label="Visits" value={s.visits} testId="analytics-card-visits" />
            <SummaryCard icon={Eye} label="Page views" value={s.page_views} testId="analytics-card-pageviews" />
            <SummaryCard icon={UserCheck} label="Unique visitors" value={s.unique_visitors} testId="analytics-card-visitors" />
            <SummaryCard icon={Inbox} label="Inquiries" value={s.inquiries} testId="analytics-card-inquiries" />
            <SummaryCard icon={Percent} label="Conversion rate" value={`${s.conversion_rate}%`} testId="analytics-card-conversion" />
            <SummaryCard icon={Globe} label="Top source" value={s.top_source || "Direct"} testId="analytics-card-source" />
          </div>

          {/* Charts */}
          <div className="mt-6 grid lg:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="analytics-visits-chart">
              <h3 className="font-display text-sm font-extrabold text-slate-900 uppercase tracking-wider">
                Visits by day
              </h3>
              <div className="mt-4 h-56">
                {data.visits_by_day.length === 0 ? (
                  <p className="text-sm text-slate-500">No visits in this range.</p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.visits_by_day}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10 }} tickLine={false} width={28} />
                      <Tooltip />
                      <Bar dataKey="visits" fill="#0369A1" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
            <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="analytics-inquiries-chart">
              <h3 className="font-display text-sm font-extrabold text-slate-900 uppercase tracking-wider">
                Inquiries by day
              </h3>
              <div className="mt-4 h-56">
                {data.inquiries_by_day.length === 0 ? (
                  <p className="text-sm text-slate-500">No inquiries in this range.</p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.inquiries_by_day}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10 }} tickLine={false} width={28} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#FF8C42" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>

          {/* Rank tables */}
          <div className="mt-6 grid md:grid-cols-2 gap-4">
            <RankTable title="Top pages" rows={data.top_pages} labelKey="page_path" testId="analytics-top-pages" />
            <RankTable
              title="CTA clicks"
              rows={data.cta_clicks.map((c) => ({ ...c, label: CTA_LABELS[c.event_type] || c.event_type }))}
              labelKey="label"
              testId="analytics-cta-clicks"
            />
            <RankTable title="Top programs" rows={data.top_programs} labelKey="name" testId="analytics-top-programs" />
            <RankTable title="Top camps" rows={data.top_camps} labelKey="name" testId="analytics-top-camps" />
          </div>

          <div className="mt-4">
            <RankTable
              title="Traffic sources / referrers"
              rows={data.referrers}
              labelKey="referrer"
              testId="analytics-referrers"
            />
          </div>

          {/* Program / camp performance */}
          <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-5 overflow-x-auto" data-testid="analytics-performance">
            <h3 className="font-display text-sm font-extrabold text-slate-900 uppercase tracking-wider">
              Program &amp; camp performance
            </h3>
            {data.performance.length === 0 ? (
              <p className="mt-3 text-sm text-slate-500">No program views yet.</p>
            ) : (
              <table className="mt-3 w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500">
                    <th className="py-2 pr-3 font-bold">Name</th>
                    <th className="py-2 pr-3 font-bold">Type</th>
                    <th className="py-2 pr-3 font-bold text-right">Views</th>
                    <th className="py-2 pr-3 font-bold text-right">Inquiries</th>
                    <th className="py-2 font-bold text-right">Conversion</th>
                  </tr>
                </thead>
                <tbody>
                  {data.performance.map((p) => (
                    <tr key={p.slug} className="border-t border-slate-100">
                      <td className="py-2.5 pr-3 font-semibold text-slate-800">{p.name}</td>
                      <td className="py-2.5 pr-3 capitalize text-slate-500">{p.type}</td>
                      <td className="py-2.5 pr-3 text-right font-bold">{p.views}</td>
                      <td className="py-2.5 pr-3 text-right font-bold">{p.inquiries}</td>
                      <td className="py-2.5 text-right text-slate-700">
                        {p.conversion_rate === null ? "—" : `${p.conversion_rate}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <p className="mt-4 text-xs text-slate-400">
            Showing {data.start} → {data.end}. Visits are unique browsing sessions; visitors are
            approximate anonymous IDs. No names, emails, or phone numbers are ever stored.
          </p>
        </>
      ) : null}
    </div>
  );
}
